/**
 * host-past — BookingsPage Past list.
 *
 * Same structure as host-upcoming; payout amount is only shown when
 * the transaction resolved successfully (state in COMPLETED_LIKE).
 * Cancelled / declined / expired bookings render without an amount —
 * showing "$X" next to "Cancelled" would imply a charge that didn't
 * actually happen.
 */
import React from 'react';

import { useIntl } from '../../../util/reactIntl';
import ResponsiveImage from '../../ResponsiveImage/ResponsiveImage';
import StatusBadge from '../StatusBadge';
import css from '../TransactionCard.module.css';
import { formatCardDateRange } from '../formatCardDateRange';

const COMPLETED_LIKE = new Set([
  'completed',
  'reviewed',
  'return-confirmed-pending-release',
  'delivered',
]);

const HostPastLayout = ({ data }) => {
  const intl = useIntl();
  const {
    state,
    processName,
    startDate,
    endDate,
    firstImage,
    imageVariants,
    listingTitle,
    customerName,
    emoji,
    payoutCents,
    serviceLabel,
  } = data;

  const showAmount =
    COMPLETED_LIKE.has(state) && typeof payoutCents === 'number';
  const amountDisplay = showAmount ? `$${(payoutCents / 100).toFixed(2)}` : null;
  const dateRange = formatCardDateRange(intl, startDate, endDate);

  return (
    <div className={`${css.cardRow} ${css.cardFull}`}>
      <div className={css.cardLeft}>
        <div className={css.thumb56}>
          {firstImage && imageVariants.length > 0 ? (
            <ResponsiveImage
              rootClassName={css.thumbImage}
              alt={listingTitle}
              image={firstImage}
              variants={imageVariants}
            />
          ) : (
            <span className={css.thumbEmoji}>{emoji}</span>
          )}
        </div>
        <div className={css.cardBody}>
          <h4 className={css.cardTitle}>{listingTitle}</h4>
          <p className={css.cardMeta}>
            {customerName}
            {dateRange ? ` · ${dateRange}` : ''}
            {serviceLabel ? ` · ${serviceLabel}` : ''}
          </p>
        </div>
      </div>
      <div className={css.cardRight}>
        {amountDisplay ? <div className={css.cardAmount}>{amountDisplay}</div> : null}
        <StatusBadge state={state} processName={processName} role="provider" />
      </div>
    </div>
  );
};

export default HostPastLayout;
